import { PartialBy } from '@upright/common/types';
import { Dictionary, values } from 'lodash';
import { ByProductCode, RestrictedAccessProduct } from '../products/types';
import {
  AndAssessmentPriorization,
  IBaseCompanyAssessment,
  MetricNature,
  PartialDict,
  PossiblyNamedModelEntityReference,
} from '../types';

export const euSheetId = '1y09MHKfbkbuKK2Vh-eKTXCl4TR_ONnI-9cr0JURJ46w';

// NOTE! Referred to from upright-sagemaker-inference-api
export enum EuObjectives {
  // Sub-objectives: subsets of specific, non-overlapping activities of EU taxonomy objectives
  // Overlapping sub-objectives are detected in getOverlappingMitigationCodes.
  // Sub- and union-objectives are kept in the same enum because typescript does not support `keyof typeof` or `key in` for keying metrics with combinatory type.
  mitigationNuclear = 'mitigationNuclear', // Certain transitional mitigation activities
  mitigationFossilGas = 'mitigationFossilGas', // Certain transitional mitigation activities
  mitigationOther = 'mitigationOther', // Other mitigation activities

  // EU taxonomy objectives
  mitigation = 'mitigation',
  adaptation = 'adaptation',
  water = 'water',
  circular = 'circular',
  pollution = 'pollution',
  biodiversity = 'biodiversity',

  // Unions of EU taxonomy objectives
  climate = 'climate', // mitigation & adaptation union
  union = 'union',
}

export type EuObjective = keyof typeof EuObjectives;
export const euObjectiveOrderFromSubsetToSuperSet: { [key in EuObjectives]: number } = {
  // Unions are adjusted before their components
  [EuObjectives.mitigationNuclear]: 1,
  [EuObjectives.mitigationFossilGas]: 1,
  [EuObjectives.mitigationOther]: 1,
  [EuObjectives.mitigation]: 2,
  [EuObjectives.adaptation]: 2,
  [EuObjectives.water]: 2,
  [EuObjectives.circular]: 2,
  [EuObjectives.pollution]: 2,
  [EuObjectives.biodiversity]: 2,
  [EuObjectives.climate]: 3,
  [EuObjectives.union]: 4,
};

// NOTE! Referred to from upright-sagemaker-inference-api
export const objectiveSetByEuObjective: { [key in EuObjectives]: EuObjectives[] } = {
  [EuObjectives.mitigationNuclear]: [EuObjectives.mitigationNuclear],
  [EuObjectives.mitigationFossilGas]: [EuObjectives.mitigationFossilGas],
  [EuObjectives.mitigationOther]: [EuObjectives.mitigationOther],
  [EuObjectives.mitigation]: [
    EuObjectives.mitigationFossilGas,
    EuObjectives.mitigationNuclear,
    EuObjectives.mitigationOther,
  ],
  [EuObjectives.adaptation]: [EuObjectives.adaptation],
  [EuObjectives.water]: [EuObjectives.water],
  [EuObjectives.circular]: [EuObjectives.circular],
  [EuObjectives.pollution]: [EuObjectives.pollution],
  [EuObjectives.biodiversity]: [EuObjectives.biodiversity],
  [EuObjectives.climate]: [EuObjectives.mitigation, EuObjectives.adaptation],
  [EuObjectives.union]: [
    EuObjectives.mitigation,
    EuObjectives.adaptation,
    EuObjectives.water,
    EuObjectives.circular,
    EuObjectives.pollution,
    EuObjectives.biodiversity,
  ],
};

export const allEuObjectives: EuObjective[] = Object.values(EuObjectives);
export const compositionalEuObjectives: EuObjective[] = [
  EuObjectives.mitigation,
  EuObjectives.climate,
  EuObjectives.union,
];
export const unionEuObjectives: EuObjective[] = [EuObjectives.climate, EuObjectives.union];
export const individualEuObjectives: EuObjective[] = [
  EuObjectives.mitigation,
  EuObjectives.adaptation,
  EuObjectives.water,
  EuObjectives.circular,
  EuObjectives.pollution,
  EuObjectives.biodiversity,
];
export const allButEuSubObjectives = [...individualEuObjectives, ...unionEuObjectives];
export const euSubObjectives: EuObjective[] = [
  EuObjectives.mitigationNuclear,
  EuObjectives.mitigationFossilGas,
  EuObjectives.mitigationOther,
];

// Each activity in the EU Taxonomy has one of three activity contribution types wrt to each objective it applies to:
// NOTE! Referred to from upright-sagemaker-inference-api
export enum EuActivityTypes {
  transitional = 'transitional',
  enabling = 'enabling',
  direct = 'direct',
  any = 'any', // union
}
export type EuActivityType = keyof typeof EuActivityTypes;
export const allEuActivityTypes: EuActivityType[] = Object.values(EuActivityTypes);
export const euActivityTypeOrderFromSubsetToSuperSet: { [key in EuActivityTypes]: number } = {
  // Unions are adjusted before their components
  [EuActivityTypes.transitional]: 1,
  [EuActivityTypes.enabling]: 1,
  [EuActivityTypes.direct]: 1,
  [EuActivityTypes.any]: 2,
};

export const nuclearByChapter: PartialDict<EuObjectives> = {
  ['4.26.']: EuObjectives.mitigationNuclear,
  ['4.27.']: EuObjectives.mitigationNuclear,
  ['4.28.']: EuObjectives.mitigationNuclear,
};

export const gasByChapter: PartialDict<EuObjectives> = {
  ['4.29.']: EuObjectives.mitigationFossilGas,
  ['4.30.']: EuObjectives.mitigationFossilGas,
  ['4.31.']: EuObjectives.mitigationFossilGas,
};

export const subObjectiveByChapter: PartialDict<EuObjectives> = {
  ...nuclearByChapter,
  ...gasByChapter,
};

export const individualEuActivityTypes = [
  EuActivityTypes.direct,
  EuActivityTypes.enabling,
  EuActivityTypes.transitional,
];
export const activityTypeSetByActivityType: { [key in EuActivityTypes]: EuActivityTypes[] } = {
  [EuActivityTypes.transitional]: [EuActivityTypes.transitional],
  [EuActivityTypes.enabling]: [EuActivityTypes.enabling],
  [EuActivityTypes.direct]: [EuActivityTypes.direct],
  [EuActivityTypes.any]: individualEuActivityTypes,
};
export const individualEuObjectiveActivityTypePairs = individualEuObjectives.flatMap(objective =>
  individualEuActivityTypes.map(activityType => ({ objective, activityType: activityType })),
);

type ByEuObjective<T> = {
  [key in EuObjective]: T;
};
type ByEuActivityType<T> = {
  [key in EuActivityTypes]: T;
};
export type ByEuObjectiveActivityType<T> = ByEuObjective<ByEuActivityType<T>>;
export type PartialByEuObjectiveActivityType<T> = Partial<ByEuObjective<Partial<ByEuActivityType<T>>>>;

// The latest version has all the metric and activity type data populated, but earlier versions might not
// NOTE! Referred to from upright-sagemaker-inference-api
export type ApiEuTaxonomyRevenueMetrics = EuTaxonomyRevenueMetrics<ApiEuObjectiveRevenueMetrics>;
export type EuTaxonomyRevenueMetrics<T = EuObjectiveRevenueMetrics> = Partial<EuTaxonomyRevenueMetricsFull<T>>;
export type EuTaxonomyRevenueMetricsFull<T = EuObjectiveRevenueMetrics> = ByEuObjective<
  EuRevenueMetricsByActivityType<T>
>;
export type EuRevenueMetricsByActivityType<T = EuObjectiveRevenueMetrics> = Partial<
  EuRevenueMetricsByActivityTypeFull<T>
>;
type EuRevenueMetricsByActivityTypeFull<T> = ByEuActivityType<T>;

export interface EuObjectiveRevenueMetrics extends EuObjectiveBaseMetrics {
  eligibility: EuTaxonomyRevenueValue;
  alignment: EuTaxonomyRevenueValue | null; //  Null only for ancient model versions
  activityType: EuActivityType;

  // Category is not stored in the data structure to avoid migration, and is indicated by the top level variable / database column only.
  // category: EuTaxonomyMetricCategory.revenue;
}

export type ApiEuObjectiveRevenueMetrics = Omit<EuObjectiveRevenueMetrics, 'eligibility'> & {
  eligibility: EuTaxonomyRevenueValue | null; // eligibility may be set to null due to access control
};

// CapEx and OpEx metrics, assessed only
export type EuTaxonomyExpenditureMetrics<T = EuObjectiveExpenditureMetrics> = Partial<
  ByEuObjective<{ [key in EuActivityTypes]?: T }> // TODO JO: Should we even try to utilize activityType, or does it break stuff too much?
>;

// CapEx or OpEx metric datum
export interface EuObjectiveExpenditureMetrics extends EuObjectiveBaseMetrics {
  /*
   * Null values indicate missing assessments (not expenditure metrics are not estimated)
   */
  eligibility: EuTaxonomyExpenditureValue | null;
  alignment: EuTaxonomyExpenditureValue | null;
  activityType: EuActivityTypes;

  // Category is not stored in the data structure to avoid migration, and is indicated by the top level variable / database column only.
  // category: EuTaxonomyMetricCategory.capex | EuTaxonomyMetricCategory.opex;
}

export interface EuObjectiveBaseMetrics {
  objective: EuObjective;
}

export const euObjectiveIdentifierFields: Array<keyof EuObjectiveRevenueMetrics> = ['objective', 'activityType'];

export enum EuTaxonomyMetricCategory {
  revenue = 'revenue',
  capex = 'capex',
  opex = 'opex',
}

// NOTE! Referred to from upright-sagemaker-inference-api
export enum EuTaxonomyMetricTypes {
  eligibility = 'eligibility',
  alignment = 'alignment',
}

export interface EuTaxonomyRevenueValue extends EuTaxonomyBaseValue {
  // estimate is our original taxonomy based estimate, without regard to disclosures or assessments.
  // It is included for debugging purposes, eg. so we can check what an eligibility value would have been without an assessment.
  estimate: number;
  lowerBound: number | null; // for value, null for eligbilities
  upperBound: number | null; // for value, null for eligbilities

  nature: MetricNature; // of value
  source?: string; // Only present when nature !== UPRIGHT_ESTIMATE
  sourceAvailable?: boolean; // true if assessment source is supported for displaying disclosure PDF links

  // Available from 0.6.0 onwards
  year?: number; // Only present when nature !== UPRIGHT_ESTIMATE
  assessedRatio: number | undefined; // Note: this key is not really present in old versions, but its set as required so we won't forget to include it in new code
}

export interface EuTaxonomyBaseValue {
  // value is the final eligibility or alignment value. It's either
  // - a pure taxonomy based estimate (value == estimate, nature == UPRIGHT_ESTIMATE),
  // - a taxonomy based estimate that's adjusted based on a disclosure/assessment of a related objective / union (value != estimate, nature == UPRIGHT_ESTIMATE),
  // - or a direct disclosure/assessment (nature != UPRIGHT_ESTIMATE).
  value: number;
  pageUrl?: string; // Only present when nature !== UPRIGHT_ESTIMATE
}

// CapEx or OpEx value datum
export interface EuTaxonomyExpenditureValue extends EuTaxonomyBaseValue {
  nature: MetricNature.DIRECT_DISCLOSURE | MetricNature.THIRD_PARTY_ASSESSMENT | MetricNature.UPRIGHT_MANUAL_ASSESSMENT; // of value
  source: string;
  year: number;
  assessedRatio: 1;
  sourceAvailable?: boolean; // true if assessment source is supported for displaying disclosure PDF links
}

export type AssessedOnlyValueDatum = Omit<EuTaxonomyRevenueValue, 'estimate' | 'lowerBound' | 'upperBound'>;

export type EuTaxonomyMetricsByCode = ByProductCode<EuTaxonomyRevenueMetrics | undefined>;

export interface EuBusinessActivity {
  title: string;
  chapter: string;
  typeByObjective: ActivityTypeByObjective;
  naceCodes: string[];
}

export type ActivityTypeByObjective = Partial<ByEuObjective<EuActivityType>>;
export type ActivityTypeByCodeAndObjective = Partial<ByProductCode<ActivityTypeByObjective>>;

export interface ProductChapter {
  productCode: string;
  chapter: string;
  naceCodes: string[]; // Codes that relate to both product and chapter
}

export type ProductChapterByCode = Dictionary<ProductChapter[]>;

export interface EligibleCompanyEuActivity {
  activity: EuBusinessActivity;
  companyRef: PossiblyNamedModelEntityReference;
  products?: RestrictedAccessProduct[];
}

export interface EuAssessment {
  id: number;
  companyId: string;
  metric: EuTaxonomyAssessmentType | EuNfrdAssessmentType | TnfaAssessmentType;
  nature: MetricNature;
  isInactive: boolean;
  year: number;
  value: number | null;
  source?: string;
  sourceUrl?: string;
  pdfUrl?: string;
  pageUrl?: string;
  rationale?: string;
  createdBy?: number;
  trace?: string;
  createdAt: string;
  updatedAt: string;
  createdByUserName?: string;
  readableBy: number;
}

export type EuAssessmentByCompanyIdMetric = Dictionary<Dictionary<EuAssessment>>;

export enum EuNfrdAssessmentType {
  // NFRD scope affects how EU taxonomy figures should be aggregated for regulators,
  // It is stored here for now to save the effort of duplicating endpoints, etc.
  NFRD_OBLIGATION = 'NFRD_OBLIGATION',
}

export function isEuNfrdAssessmentType(
  euAssessmentType: EuNfrdAssessmentType | EuTaxonomyAssessmentType | TnfaAssessmentType,
): euAssessmentType is EuNfrdAssessmentType {
  return euAssessmentType === EuNfrdAssessmentType.NFRD_OBLIGATION;
}

// Nuclear and Fossil Gas Activity assessments (binary Yes/No disclosures)
export enum TnfaAssessmentType {
  // Nuclear energy-related activities
  TNFA_NUCLEAR_INNOVATIVE_ELECTRICITY = 'TNFA_NUCLEAR_INNOVATIVE_ELECTRICITY',
  TNFA_NUCLEAR_NEW_INSTALLATIONS = 'TNFA_NUCLEAR_NEW_INSTALLATIONS',
  TNFA_NUCLEAR_EXISTING_INSTALLATIONS = 'TNFA_NUCLEAR_EXISTING_INSTALLATIONS',
  // Fossil gas-related activities
  TNFA_FOSSIL_ELECTRICITY = 'TNFA_FOSSIL_ELECTRICITY',
  TNFA_FOSSIL_COMBINED_HEAT_POWER = 'TNFA_FOSSIL_COMBINED_HEAT_POWER',
  TNFA_FOSSIL_HEAT_GENERATION = 'TNFA_FOSSIL_HEAT_GENERATION',
}

export function isTnfaAssessmentType(
  assessmentType: EuNfrdAssessmentType | EuTaxonomyAssessmentType | TnfaAssessmentType,
): assessmentType is TnfaAssessmentType {
  return Object.values(TnfaAssessmentType).includes(assessmentType as TnfaAssessmentType);
}

export const allTnfaAssessmentTypes = Object.values(TnfaAssessmentType);

export function getEuTaxonomyAssessmentType(
  objective: EuObjectives,
  category: EuTaxonomyMetricCategory,
  alignmentType: EuTaxonomyMetricTypes,
  activityType: EuActivityTypes,
): EuTaxonomyAssessmentType {
  function getObjectiveTypeString(): string {
    switch (objective) {
      case EuObjectives.climate:
        return 'MITIGATION_OR_ADAPTATION';
      case EuObjectives.mitigationFossilGas:
      case EuObjectives.mitigationOther:
        throw Error(`Not supported`);
      case EuObjectives.adaptation:
      case EuObjectives.biodiversity:
      case EuObjectives.circular:
      case EuObjectives.mitigation:
      case EuObjectives.union:
      case EuObjectives.water:
      case EuObjectives.mitigationNuclear:
      case EuObjectives.pollution:
      default:
        return objective.toUpperCase();
    }
  }
  function getActivityTypeString(): string {
    switch (activityType) {
      case EuActivityTypes.enabling:
        return '_ENABLING';
      case EuActivityTypes.transitional:
        return '_TRANSITIONAL';
      case EuActivityTypes.any:
      case EuActivityTypes.direct:
      default:
        return '';
    }
  }
  return `${getObjectiveTypeString()}_${category.toUpperCase()}_${alignmentType.toUpperCase()}${getActivityTypeString()}` as EuTaxonomyAssessmentType;
}

// NOTE: Keep in sync with getEuTaxonomyAssessmentType function above
// NOTE: Make sure all values contain all necessary words ("revenue", "capex", "alignment"...) for the worry-free parsing below
export enum EuTaxonomyAssessmentType {
  // For now, all assessments represent total eligibility over all activity types (enabling, transition, direct)
  MITIGATION_REVENUE_ELIGIBILITY = 'MITIGATION_REVENUE_ELIGIBILITY',
  MITIGATION_REVENUE_ALIGNMENT = 'MITIGATION_REVENUE_ALIGNMENT',
  ADAPTATION_REVENUE_ELIGIBILITY = 'ADAPTATION_REVENUE_ELIGIBILITY',
  ADAPTATION_REVENUE_ALIGNMENT = 'ADAPTATION_REVENUE_ALIGNMENT',
  WATER_REVENUE_ELIGIBILITY = 'WATER_REVENUE_ELIGIBILITY',
  WATER_REVENUE_ALIGNMENT = 'WATER_REVENUE_ALIGNMENT',
  CIRCULAR_REVENUE_ELIGIBILITY = 'CIRCULAR_REVENUE_ELIGIBILITY',
  CIRCULAR_REVENUE_ALIGNMENT = 'CIRCULAR_REVENUE_ALIGNMENT',
  POLLUTION_REVENUE_ELIGIBILITY = 'POLLUTION_REVENUE_ELIGIBILITY',
  POLLUTION_REVENUE_ALIGNMENT = 'POLLUTION_REVENUE_ALIGNMENT',
  BIODIVERSITY_REVENUE_ELIGIBILITY = 'BIODIVERSITY_REVENUE_ELIGIBILITY',
  BIODIVERSITY_REVENUE_ALIGNMENT = 'BIODIVERSITY_REVENUE_ALIGNMENT',
  MITIGATION_OR_ADAPTATION_REVENUE_ELIGIBILITY = 'MITIGATION_OR_ADAPTATION_REVENUE_ELIGIBILITY',
  MITIGATION_OR_ADAPTATION_REVENUE_ALIGNMENT = 'MITIGATION_OR_ADAPTATION_REVENUE_ALIGNMENT',
  UNION_REVENUE_ELIGIBILITY = 'UNION_REVENUE_ELIGIBILITY',
  UNION_REVENUE_ALIGNMENT = 'UNION_REVENUE_ALIGNMENT',
  UNION_REVENUE_ALIGNMENT_ENABLING = 'UNION_REVENUE_ALIGNMENT_ENABLING',
  UNION_REVENUE_ALIGNMENT_TRANSITIONAL = 'UNION_REVENUE_ALIGNMENT_TRANSITIONAL',

  MITIGATION_CAPEX_ELIGIBILITY = 'MITIGATION_CAPEX_ELIGIBILITY',
  MITIGATION_CAPEX_ALIGNMENT = 'MITIGATION_CAPEX_ALIGNMENT',
  ADAPTATION_CAPEX_ELIGIBILITY = 'ADAPTATION_CAPEX_ELIGIBILITY',
  ADAPTATION_CAPEX_ALIGNMENT = 'ADAPTATION_CAPEX_ALIGNMENT',
  WATER_CAPEX_ELIGIBILITY = 'WATER_CAPEX_ELIGIBILITY',
  WATER_CAPEX_ALIGNMENT = 'WATER_CAPEX_ALIGNMENT',
  CIRCULAR_CAPEX_ELIGIBILITY = 'CIRCULAR_CAPEX_ELIGIBILITY',
  CIRCULAR_CAPEX_ALIGNMENT = 'CIRCULAR_CAPEX_ALIGNMENT',
  POLLUTION_CAPEX_ELIGIBILITY = 'POLLUTION_CAPEX_ELIGIBILITY',
  POLLUTION_CAPEX_ALIGNMENT = 'POLLUTION_CAPEX_ALIGNMENT',
  BIODIVERSITY_CAPEX_ELIGIBILITY = 'BIODIVERSITY_CAPEX_ELIGIBILITY',
  BIODIVERSITY_CAPEX_ALIGNMENT = 'BIODIVERSITY_CAPEX_ALIGNMENT',
  MITIGATION_OR_ADAPTATION_CAPEX_ELIGIBILITY = 'MITIGATION_OR_ADAPTATION_CAPEX_ELIGIBILITY',
  MITIGATION_OR_ADAPTATION_CAPEX_ALIGNMENT = 'MITIGATION_OR_ADAPTATION_CAPEX_ALIGNMENT',
  UNION_CAPEX_ELIGIBILITY = 'UNION_CAPEX_ELIGIBILITY',
  UNION_CAPEX_ALIGNMENT = 'UNION_CAPEX_ALIGNMENT',
  UNION_CAPEX_ALIGNMENT_ENABLING = 'UNION_CAPEX_ALIGNMENT_ENABLING',
  UNION_CAPEX_ALIGNMENT_TRANSITIONAL = 'UNION_CAPEX_ALIGNMENT_TRANSITIONAL',

  MITIGATION_OPEX_ELIGIBILITY = 'MITIGATION_OPEX_ELIGIBILITY',
  MITIGATION_OPEX_ALIGNMENT = 'MITIGATION_OPEX_ALIGNMENT',
  ADAPTATION_OPEX_ELIGIBILITY = 'ADAPTATION_OPEX_ELIGIBILITY',
  ADAPTATION_OPEX_ALIGNMENT = 'ADAPTATION_OPEX_ALIGNMENT',
  WATER_OPEX_ELIGIBILITY = 'WATER_OPEX_ELIGIBILITY',
  WATER_OPEX_ALIGNMENT = 'WATER_OPEX_ALIGNMENT',
  CIRCULAR_OPEX_ELIGIBILITY = 'CIRCULAR_OPEX_ELIGIBILITY',
  CIRCULAR_OPEX_ALIGNMENT = 'CIRCULAR_OPEX_ALIGNMENT',
  POLLUTION_OPEX_ELIGIBILITY = 'POLLUTION_OPEX_ELIGIBILITY',
  POLLUTION_OPEX_ALIGNMENT = 'POLLUTION_OPEX_ALIGNMENT',
  BIODIVERSITY_OPEX_ELIGIBILITY = 'BIODIVERSITY_OPEX_ELIGIBILITY',
  BIODIVERSITY_OPEX_ALIGNMENT = 'BIODIVERSITY_OPEX_ALIGNMENT',
  MITIGATION_OR_ADAPTATION_OPEX_ELIGIBILITY = 'MITIGATION_OR_ADAPTATION_OPEX_ELIGIBILITY',
  MITIGATION_OR_ADAPTATION_OPEX_ALIGNMENT = 'MITIGATION_OR_ADAPTATION_OPEX_ALIGNMENT',
  UNION_OPEX_ELIGIBILITY = 'UNION_OPEX_ELIGIBILITY',
  UNION_OPEX_ALIGNMENT = 'UNION_OPEX_ALIGNMENT',
  UNION_OPEX_ALIGNMENT_ENABLING = 'UNION_OPEX_ALIGNMENT_ENABLING',
  UNION_OPEX_ALIGNMENT_TRANSITIONAL = 'UNION_OPEX_ALIGNMENT_TRANSITIONAL',

  NUCLEAR_ALIGNMENT = 'NUCLEAR_REVENUE_ALIGNMENT',
  FOSSIL_FUEL_ALIGNMENT = 'FOSSIL_FUEL_REVENUE_ALIGNMENT',
}

export function isEuTaxonomyAssessmentType(value: any): value is EuTaxonomyAssessmentType {
  return value in EuTaxonomyAssessmentType;
}

export function assessmentTypesByCategoryMetricObjective2(
  category?: EuTaxonomyMetricCategory,
  metric?: EuTaxonomyMetricTypes,
  objective?: EuObjectives,
  activityType?: EuActivityTypes,
) {
  return Object.values(EuTaxonomyAssessmentType).filter(t => {
    if (category && getCategory(t) !== category) return false;
    if (metric && getMetric(t) !== metric) return false;
    if (objective && getObjective(t) !== objective) return false;
    if (activityType && getActivityType(t) !== activityType) return false;
    return true;
  });
}

export const assessmentTypeByCategoryMetricObjective: Record<
  EuTaxonomyMetricCategory,
  Record<EuTaxonomyMetricTypes, Partial<Record<EuObjectives, EuTaxonomyAssessmentType>>>
> = {
  [EuTaxonomyMetricCategory.revenue]: {
    [EuTaxonomyMetricTypes.eligibility]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_REVENUE_ELIGIBILITY,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_REVENUE_ELIGIBILITY,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_REVENUE_ELIGIBILITY,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_REVENUE_ELIGIBILITY,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_REVENUE_ELIGIBILITY,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_REVENUE_ELIGIBILITY,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_REVENUE_ELIGIBILITY,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_REVENUE_ELIGIBILITY,
    },
    [EuTaxonomyMetricTypes.alignment]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_REVENUE_ALIGNMENT,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_REVENUE_ALIGNMENT,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_REVENUE_ALIGNMENT,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_REVENUE_ALIGNMENT,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_REVENUE_ALIGNMENT,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_REVENUE_ALIGNMENT,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_REVENUE_ALIGNMENT,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_REVENUE_ALIGNMENT,
    },
  },
  [EuTaxonomyMetricCategory.opex]: {
    [EuTaxonomyMetricTypes.eligibility]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_OPEX_ELIGIBILITY,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_OPEX_ELIGIBILITY,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_OPEX_ELIGIBILITY,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_OPEX_ELIGIBILITY,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_OPEX_ELIGIBILITY,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_OPEX_ELIGIBILITY,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_OPEX_ELIGIBILITY,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_OPEX_ELIGIBILITY,
    },
    [EuTaxonomyMetricTypes.alignment]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_OPEX_ALIGNMENT,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_OPEX_ALIGNMENT,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_OPEX_ALIGNMENT,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_OPEX_ALIGNMENT,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_OPEX_ALIGNMENT,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_OPEX_ALIGNMENT,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_OPEX_ALIGNMENT,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_OPEX_ALIGNMENT,
    },
  },
  [EuTaxonomyMetricCategory.capex]: {
    [EuTaxonomyMetricTypes.eligibility]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_CAPEX_ELIGIBILITY,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_CAPEX_ELIGIBILITY,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_CAPEX_ELIGIBILITY,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_CAPEX_ELIGIBILITY,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_CAPEX_ELIGIBILITY,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_CAPEX_ELIGIBILITY,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_CAPEX_ELIGIBILITY,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_CAPEX_ELIGIBILITY,
    },
    [EuTaxonomyMetricTypes.alignment]: {
      [EuObjectives.mitigation]: EuTaxonomyAssessmentType.MITIGATION_CAPEX_ALIGNMENT,
      [EuObjectives.adaptation]: EuTaxonomyAssessmentType.ADAPTATION_CAPEX_ALIGNMENT,
      [EuObjectives.water]: EuTaxonomyAssessmentType.WATER_CAPEX_ALIGNMENT,
      [EuObjectives.circular]: EuTaxonomyAssessmentType.CIRCULAR_CAPEX_ALIGNMENT,
      [EuObjectives.pollution]: EuTaxonomyAssessmentType.POLLUTION_CAPEX_ALIGNMENT,
      [EuObjectives.biodiversity]: EuTaxonomyAssessmentType.BIODIVERSITY_CAPEX_ALIGNMENT,
      [EuObjectives.climate]: EuTaxonomyAssessmentType.MITIGATION_OR_ADAPTATION_CAPEX_ALIGNMENT,
      [EuObjectives.union]: EuTaxonomyAssessmentType.UNION_CAPEX_ALIGNMENT,
    },
  },
};

// Easygoing parsing functions for reading EuTaxonomyAssessmentType: //

function getCategory(assessmentType: EuTaxonomyAssessmentType) {
  for (const category of values(EuTaxonomyMetricCategory)) {
    if (assessmentType.includes(category.toUpperCase())) {
      return category;
    }
  }
  throw Error(`Could not figure out category for assessment type: ${assessmentType}`);
}

function getMetric(assessmentType: EuTaxonomyAssessmentType) {
  for (const metric of values(EuTaxonomyMetricTypes)) {
    if (assessmentType.includes(metric.toUpperCase())) {
      return metric;
    }
  }
  throw Error(`Could not figure out metric for assessment type: ${assessmentType}`);
}

const objectivePrefixes = {
  MITIGATION_OR_ADAPTATION: EuObjectives.climate,
  MITIGATION: EuObjectives.mitigation,
  ADAPTATION: EuObjectives.adaptation,
  WATER: EuObjectives.water,
  CIRCULAR: EuObjectives.circular,
  POLLUTION: EuObjectives.pollution,
  BIODIVERSITY: EuObjectives.biodiversity,
  UNION: EuObjectives.union,
  NUCLEAR: EuObjectives.mitigationNuclear, // TODO ML JO: Can we reuse these?
  FOSSIL_FUEL: EuObjectives.mitigationFossilGas, // TODO ML JO: Can we reuse these?
};
function getObjective(assessmentType: EuTaxonomyAssessmentType) {
  for (const [prefix, objective] of Object.entries(objectivePrefixes)) {
    if (assessmentType.includes(prefix.toUpperCase())) {
      return objective;
    }
  }
  throw Error(`Could not figure out objective for assessment type: ${assessmentType}`);
}

const activityTags = {
  ENABLING: EuActivityTypes.enabling,
  TRANSITIONAL: EuActivityTypes.transitional,
  DIRECT: EuActivityTypes.direct,
  // otherwise 'any'
};
export function getActivityType(assessmentType: EuTaxonomyAssessmentType) {
  for (const [prefix, activityType] of Object.entries(activityTags)) {
    if (assessmentType.includes(prefix.toUpperCase())) {
      return activityType;
    }
  }
  return EuActivityTypes.any;
}

export function getAssessmentTypesOfCategory(category: EuTaxonomyMetricCategory) {
  return values(EuTaxonomyAssessmentType).filter(t => getCategory(t) === category);
}

export function splitAssessmentType(assessmentType: EuTaxonomyAssessmentType) {
  const category = getCategory(assessmentType);
  const metric = getMetric(assessmentType);
  const objective = getObjective(assessmentType);
  const activityType = getActivityType(assessmentType);
  return { category, metric, objective, activityType };
}

export const allEuObjectiveAssessmentTypes = Object.values(EuTaxonomyAssessmentType);

export const usedEuNfrdAssessmentTypes = [...Object.values(EuNfrdAssessmentType)];

export type EuAssessmentWithPrioritization = EuAssessment & AndAssessmentPriorization;

// NOTE: keep consistent with joiEuTaxonomyAssessment
export interface IEuAssessment extends IBaseCompanyAssessment {
  metric: EuTaxonomyAssessmentType | EuNfrdAssessmentType | TnfaAssessmentType;
}

export interface IEuTaxonomyAssessment extends IEuAssessment {
  metric: EuTaxonomyAssessmentType;
}

export interface INfrdAssessment extends IEuAssessment {
  metric: EuNfrdAssessmentType;
}

export interface ITnfaAssessment extends IEuAssessment {
  metric: TnfaAssessmentType;
}

export type CreateTaxonomyAssessmentRequest = PartialBy<
  Omit<IEuTaxonomyAssessment | INfrdAssessment | ITnfaAssessment, 'id'>,
  'createdBy'
>;
export type CreateTaxonomyAssessmentsRequest = CreateTaxonomyAssessmentRequest[];

// NOTE: keep consistent with joiUpdateEuAssessmentPayload
export interface UpdateEuAssessmentPayload {
  companyId?: string;
  nature?: MetricNature;
  year?: number;
  value?: number | null;
  source?: string;
  sourceUrl?: string;
  rationale?: string;
  isInactive?: boolean;
}

export interface UpdateEuAssessmentAccessPayload {
  readableBy: number;
}
